import type { AiChatContext } from './context'

type QuestionAnswer = {
  selectedOptionId?: string | null
  customAnswer?: string | null
}

/** Handles V2 question cards and resumes the persisted AgentLoop checkpoint. */
export function usePendingUserQuestions(
  ctx: AiChatContext,
  resumeQuestion: (
    pendingQuestionId: string,
    userChoice?: string,
    extraPayload?: Record<string, unknown>
  ) => Promise<void>
): {
  answerPendingUserQuestion: (payload: QuestionAnswer) => Promise<void>
  stageAndGoNext: (payload: QuestionAnswer) => void
  goToPrev: () => void
} {
  const {
    sessionId,
    lastError,
    pendingUserQuestion,
    pendingQuestionQueue,
    stageAnswer,
    getAllStagedAnswers,
    goToNextQuestion,
    goToPrevQuestion
  } = ctx

  function answerText(payload: QuestionAnswer): string {
    return payload.customAnswer?.trim() || payload.selectedOptionId?.trim() || ''
  }

  function isCurrentSession(): boolean {
    const pending = pendingUserQuestion.value
    return Boolean(
      pending &&
      sessionId.value?.trim() &&
      pending.conversationSessionId === sessionId.value?.trim()
    )
  }

  function collectGroupAnswers(): Array<{ pendingQuestionId: string; userChoice: string }> {
    const stagedById = new Map(
      getAllStagedAnswers().map((answer) => [answer.pendingQuestionId, answer])
    )
    return pendingQuestionQueue.value
      .map((question) => ({
        pendingQuestionId: question.id,
        userChoice: answerText(stagedById.get(question.id) ?? {})
      }))
      .filter((answer) => answer.userChoice.length > 0)
  }

  async function answerPendingUserQuestion(payload: QuestionAnswer): Promise<void> {
    const pending = pendingUserQuestion.value
    if (!pending || !isCurrentSession()) return
    const userChoice = answerText(payload)
    lastError.value = null

    if (pendingQuestionQueue.value.length > 1 && pending.questionGroupId) {
      stageAnswer(pending.id, payload)
      const groupAnswers = collectGroupAnswers()
      await resumeQuestion(pending.id, userChoice, { groupAnswers })
      return
    }
    await resumeQuestion(pending.id, userChoice)
  }

  function stageAndGoNext(payload: QuestionAnswer): void {
    const pending = pendingUserQuestion.value
    if (!pending) return
    stageAnswer(pending.id, payload)
    goToNextQuestion()
  }

  function goToPrev(): void {
    goToPrevQuestion()
  }

  return {
    answerPendingUserQuestion,
    stageAndGoNext,
    goToPrev
  }
}
